/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import debounce from 'lodash/debounce';

import {useSearchSuggestions} from '@salesforce/commerce-sdk-react';
import {SearchIcon, SubmitSearchIcon} from '@salesforce/retail-react-app/app/components/icons';
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive';
import SearchSuggestions from '@salesforce/retail-react-app/app/components/search/partials/search-suggestions';
import {
  Box, Button, Divider, Flex, HStack, Input, Popover,
  PopoverContent, PopoverTrigger, Spinner, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {RECENT_SEARCH_KEY, RECENT_SEARCH_LIMIT, RECENT_SEARCH_MIN_LENGTH} from '@salesforce/retail-react-app/app/constants';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import {categoryUrlBuilder, productUrlBuilder, searchUrlBuilder} from '@salesforce/retail-react-app/app/utils/url';
import {boldString, capitalize, getSessionJSONItem, setSessionJSONItem} from '@salesforce/retail-react-app/app/utils/utils';

const formatSuggestions = (searchSuggestions, input) => {
  return {
    categorySuggestions: searchSuggestions?.categorySuggestions?.categories?.map(
      (suggestion) => {
        return {
          type: 'category',
          id: suggestion.id,
          link: categoryUrlBuilder({id: suggestion.id}),
          name: boldString(suggestion.name, capitalize(input)),
        };
      }
    ),
    productSuggestions: searchSuggestions?.productSuggestions?.products?.map((product) => {
      return {
        type: 'product',
        currency: product.currency,
        price: product.price,
        productId: product.productId,
        name: boldString(product.productName, capitalize(input)),
        link: productUrlBuilder({id: product.productId}),
      };
    }),
    phraseSuggestions: searchSuggestions?.categorySuggestions?.suggestedPhrases?.map(
      (phrase) => {
        return {
          type: 'phrase',
          name: boldString(phrase.phrase, capitalize(input)),
          link: searchUrlBuilder(phrase.phrase),
        };
      }
    ),
  };
};

/**
 * The SearchInput component is a stylized
 * text input made specifically for use in
 * the application header.
 * @param  {object} props
 * @param  {object} props.inputRef - External ref for the search input (for focus management)
 * @param  {func} props.onCloseModal - Callback to close the parent modal
 * @return  {React.ReactElement} - SearchInput component
 */
const Search = ({onCloseModal, inputRef, ...props}) => { // eslint-disable-line react/prop-types
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigation();
  const searchSuggestion = useSearchSuggestions(
    {
      parameters: {
        q: searchQuery,
      },
    },
    {
      enabled: searchQuery?.length >= RECENT_SEARCH_MIN_LENGTH,
    }
  );
  // Use external ref if provided, otherwise use internal ref
  const internalRef = useRef();
  const searchInputRef = inputRef || internalRef;
  const recentSearches = getSessionJSONItem(RECENT_SEARCH_KEY);
  const searchSuggestions = useMemo(
    () => formatSuggestions(searchSuggestion.data, searchInputRef?.current?.value),
    [searchSuggestion]
  );

  // check if popover should open if we have suggestions
  useEffect(() => {
    shouldOpenPopover();
  }, [searchQuery, searchSuggestion.data]);

  const searchSuggestionsAvailable =
    searchSuggestions &&
    (searchSuggestions?.categorySuggestions?.length ||
      searchSuggestions?.phraseSuggestions?.length);

  const saveRecentSearch = (searchText) => {
    // Get recent searches or an empty array if undefined.
    let searches = getSessionJSONItem(RECENT_SEARCH_KEY) || [];

    // Check if term is already in the saved searches
    searches = searches.filter((savedSearchTerm) => {
      return searchText.toLowerCase() !== savedSearchTerm.toLowerCase();
    });

    // Create a new array consisting of the search text and up to 4 other resent searches.
    // I'm assuming the order is newest to oldest.
    searches = [searchText, ...searches].slice(0, RECENT_SEARCH_LIMIT);

    // Replace the save resent search with the updated value.
    setSessionJSONItem(RECENT_SEARCH_KEY, searches);
  };

  const debouncedSearch = debounce((input) => {
    debouncedSearch.cancel();
    setSearchQuery(input);
  }, 300);

  const onSearchChange = async (e) => {
    const input = e.target.value;

    if (input.length >= RECENT_SEARCH_MIN_LENGTH) {
      debouncedSearch(input);
    } else {
      setSearchQuery('');
    }
  };

  const clearInput = () => {
    searchInputRef.current.blur();
    setIsOpen(false);
  };

  const onSubmitSearch = (e) => {
    e.preventDefault();
    // Avoid blank spaces to be searched
    let searchText = searchInputRef.current.value.trim();

    // Avoid empty string searches
    if (searchText.length < 1) {
      return;
    }
    saveRecentSearch(searchText);
    clearInput();
    onCloseModal();
    navigate(searchUrlBuilder(searchText));
  };

  const closeAndNavigate = (link) => {
    if (onCloseModal) {
      onCloseModal();
    }

    if (!link) {
      clearInput();
      setIsOpen(false);
    } else {
      clearInput();
      setIsOpen(false);
      navigate(link);
    }
  };

  const shouldOpenPopover = () => {
    // As per design we only want to show the popover if the input is focused and we have recent searches saved
    // or we have search suggestions available and have inputed some text (empty text in this scenario should show recent searches)
    if (
      (document.activeElement.id === 'search-input' && recentSearches?.length > 0) ||
      (searchSuggestionsAvailable && searchInputRef.current.value.length > 0)
    ) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const onSearchInputChange = (e) => {
    onSearchChange(e);
    shouldOpenPopover();
  };

  const styles = useMultiStyleConfig('SearchForm');

  const intl = useIntl();
  const placeholder = intl.formatMessage({
    id: 'search.field.placeholder',
    defaultMessage: 'Search King',
  });

  return (
    <Box>
      <Popover initialFocusRef={searchInputRef} isOpen={isOpen} isLazy>
        <PopoverTrigger>
          <form onSubmit={onSubmitSearch}>
            <Box>
              <HStack {...styles.inputContainer}>
                <Box {...styles.iconWrapper}>
                  <SearchIcon />
                </Box>
                <Input
                  ref={searchInputRef}
                  autoComplete="off"
                  id="search-input"
                  placeholder={placeholder}
                  type="input"
                  onChange={(e) => onSearchInputChange(e)}
                  {...styles.input}
                  {...props}
                />
                {searchInputRef.current?.value && (
                  <>
                    <Button onClick={clearInput} {...styles.clearButton}>
                      <FormattedMessage
                        defaultMessage="Clear"
                        id="search.action.clear"
                      />
                    </Button>
                    <Button onClick={onSubmitSearch} {...styles.submitButton}>
                      <SubmitSearchIcon />
                    </Button>
                  </>
                )}
              </HStack>
              <Divider {...styles.divider} />
            </Box>
          </form>
        </PopoverTrigger>

        <HideOnMobile>
          <PopoverContent data-testid="sf-suggestion-popover">
            <SearchSuggestions
              closeAndNavigate={closeAndNavigate}
              recentSearches={recentSearches}
              searchSuggestions={searchSuggestions}
            />
          </PopoverContent>
        </HideOnMobile>
      </Popover>
      <HideOnDesktop>
        <Flex
          background="white"
          display={isOpen || searchInputRef?.value?.length > 0 ? 'block' : 'none'}
          height="100vh"
          left={0}
          postion="absolute"
          right={0}
        >
          {searchSuggestion.isLoading ? (
            <Spinner
              color="blue.600"
              left="50%"
              margin="-25px 0 0 -25px"
              opacity={0.85}
              position="absolute"
              top="50%"
              zIndex="9999"
            />
          ) : (
            <SearchSuggestions
              closeAndNavigate={closeAndNavigate}
              recentSearches={recentSearches}
              searchSuggestions={searchSuggestions}
            />
          )}
        </Flex>
      </HideOnDesktop>
    </Box>
  );
};

Search.displayName = 'SearchInput';

export default Search;
